import { LocalDate } from "@js-joda/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { SalaryNotice } from "model/Salary";
import { Api } from "services/api/api.service";

export type MonthlySalaryFetchState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "loaded";
      salaryNotices: SalaryNotice[];
      hasMore: boolean;
      isLoadingMore: boolean;
    }
  | { status: "error" };

export type MonthlySalaryLoadedState = Extract<
  MonthlySalaryFetchState,
  { status: "loaded" }
>;

interface UseMonthlySalaryReturn {
  fetchState: MonthlySalaryFetchState;
  /** Updates a single worker's notice in place, keeping the loaded pages. */
  refreshWorkerNotice: (workerId: number) => void;
  loadMore: () => void;
}

const FETCH_AMOUNT = 25;

const mergeSalaryNoticesById = (
  previousNotices: SalaryNotice[],
  nextNotices: SalaryNotice[],
): SalaryNotice[] => {
  const mergedNotices = new Map<number, SalaryNotice>();
  previousNotices.forEach((notice) => {
    mergedNotices.set(notice.id, notice);
  });
  nextNotices.forEach((notice) => {
    mergedNotices.set(notice.id, notice);
  });
  return Array.from(mergedNotices.values());
};

export const useMonthlySalary = (
  companyId: number | undefined,
  currentDate: LocalDate,
  refreshKey: number,
): UseMonthlySalaryReturn => {
  // Bumping refreshKey (notices generated for the whole month) invalidates the
  // accumulated pages just like a company or period switch does.
  const periodKey = `${companyId ?? "none"}-${currentDate.year()}-${currentDate.monthValue()}-${refreshKey}`;
  const [pageState, setPageState] = useState({ periodKey, page: 0 });
  const [fetchState, setFetchState] = useState<MonthlySalaryFetchState>({
    status: "idle",
  });
  const latestRequestIdRef = useRef(0);

  // Fall back to the first page until the page state catches up with the key.
  const page = pageState.periodKey === periodKey ? pageState.page : 0;

  const fetchSalaries = useCallback(
    (pageToFetch: number, append: boolean): void => {
      if (!companyId) {
        latestRequestIdRef.current += 1;
        setFetchState({ status: "idle" });
        return;
      }

      const requestId = ++latestRequestIdRef.current;
      setFetchState((previousState) =>
        append && previousState.status === "loaded"
          ? { ...previousState, isLoadingMore: true }
          : { status: "loading" },
      );

      const loadSalaries = async (): Promise<void> => {
        try {
          const res = await Api().company.salary.getSalaries(
            companyId,
            pageToFetch,
            FETCH_AMOUNT,
            currentDate.year(),
            currentDate.monthValue(),
          );
          if (requestId !== latestRequestIdRef.current) return;
          setFetchState((previousState) => {
            const salaryNotices =
              append && previousState.status === "loaded"
                ? mergeSalaryNoticesById(
                    previousState.salaryNotices,
                    res.data.salaryNotices,
                  )
                : res.data.salaryNotices;
            return {
              status: "loaded",
              salaryNotices,
              hasMore: salaryNotices.length < res.data.total,
              isLoadingMore: false,
            };
          });
        } catch {
          if (requestId !== latestRequestIdRef.current) return;
          setFetchState({ status: "error" });
        }
      };

      void loadSalaries();
    },
    [companyId, currentDate],
  );

  // periodKey is a dependency so a refreshKey bump refetches even when the list
  // is already sitting on page 0.
  useEffect(() => {
    fetchSalaries(page, page > 0);
  }, [fetchSalaries, page, periodKey]);

  const loadMore = useCallback((): void => {
    if (fetchState.status !== "loaded") return;
    if (!fetchState.hasMore || fetchState.isLoadingMore) return;
    setPageState({ periodKey, page: page + 1 });
  }, [fetchState, page, periodKey]);

  const refreshWorkerNotice = useCallback(
    (workerId: number): void => {
      if (!companyId) return;

      const loadWorkerNotice = async (): Promise<void> => {
        try {
          const res = await Api().company.salary.getSalaries(
            companyId,
            0,
            1,
            currentDate.year(),
            currentDate.monthValue(),
            workerId,
          );
          const updatedNotice = res.data.salaryNotices[0];
          if (!updatedNotice) return;
          setFetchState((previousState) =>
            previousState.status === "loaded"
              ? {
                  ...previousState,
                  salaryNotices: previousState.salaryNotices.map((notice) =>
                    notice.id === updatedNotice.id ? updatedNotice : notice,
                  ),
                }
              : previousState,
          );
        } catch (e) {
          console.error("Error when refreshing salary notice", e);
        }
      };

      void loadWorkerNotice();
    },
    [companyId, currentDate],
  );

  return {
    fetchState,
    refreshWorkerNotice,
    loadMore,
  };
};
